/**
 * Reading Plan card (Grow tab) — v3 layout: horizontal day strip on top,
 * passages for the selected day below.
 *
 * Day strip: 7 little circular day-buttons in a row, today's day in the
 * visual middle when the window is symmetric. Tapping a day selects it
 * and renders its passages below with a "Day X of XX" subtitle. The
 * default selection is today.
 *
 * Passage row: checkbox · ref · external-link icon (replaces the v2
 * text "Read →" link). Tapping the icon optimistically marks the
 * passage done AND opens YouVersion; tapping the checkbox toggles the
 * mark without opening anything.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ConfirmDialog } from './ConfirmDialog';
import { Sheet } from './Sheet';
import {
  readingPlanMeQueryOptions,
  readingPlansListQueryOptions,
  type ReadingPlanCatalogEntry,
  type ReadingPlanMeResponse,
  type ReadingPlanPassage,
  type ReadingPlanWeekDay,
} from '../lib/queries';

/** Bible translations the YouVersion URL builder + members.translation
 *  field both know about. Keep in sync with TRANSLATION_ID in
 *  apps/api/src/lib/youversion.ts. */
const TRANSLATIONS = ['CSB', 'ESV', 'NIV', 'NLT', 'KJV', 'NASB'] as const;

export function DailyReadingCard() {
  const me = useQuery(readingPlanMeQueryOptions());
  if (me.isLoading) {
    return (
      <section className="card p-5">
        <h2 className="font-serif text-2xl text-ink">Reading Plan</h2>
        <p className="text-soft text-sm mt-2">Loading…</p>
      </section>
    );
  }
  if (!me.data) return null;
  const data = me.data;

  if (!data.subscription) return <PickerCard />;
  if (data.endReached) return <EndReachedCard data={data} />;
  return <WeekCard data={data} />;
}

// ----------------------------------------------------------------- Picker
function PickerCard() {
  const qc = useQueryClient();
  const subscribe = useMutation({
    mutationFn: (planSlug: string) =>
      api.post('/reading-plans/me/subscribe', { planSlug }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] });
    },
  });
  const catalog = useQuery(readingPlansListQueryOptions());
  const plans = catalog.data?.plans ?? [];
  return (
    <section className="card p-5">
      <h2 className="font-serif text-2xl text-ink">Reading Plan</h2>
      <p className="font-serif italic text-soft mt-1">
        Pick a plan and read the Word with your family.
      </p>
      <div className="mt-4 space-y-2">
        {plans.map((p) => (
          <button
            key={p.slug}
            onClick={() => subscribe.mutate(p.slug)}
            disabled={subscribe.isPending}
            className="card w-full p-4 text-left hover:bg-line/40 disabled:opacity-50"
          >
            <div className="font-serif text-lg text-ink">{p.name}</div>
            <div className="text-xs text-muted mt-1">
              {p.totalDays} days · ~{durationLabel(p.totalDays)}
            </div>
          </button>
        ))}
        {!catalog.isLoading && plans.length === 0 && (
          <p className="text-soft text-sm italic">No reading plans are available yet.</p>
        )}
      </div>
    </section>
  );
}

function durationLabel(totalDays: number): string {
  if (totalDays >= 1000) return '3 years';
  if (totalDays >= 700) return '2 years';
  if (totalDays >= 350) return '1 year';
  return `${Math.round(totalDays / 30)} months`;
}

// -------------------------------------------------------------- End reached
function EndReachedCard({ data }: { data: ReadingPlanMeResponse }) {
  const qc = useQueryClient();
  const pickAnother = useMutation({
    mutationFn: () => api.delete('/reading-plans/me/subscribe'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] }),
  });
  return (
    <section className="card p-5 text-center">
      <h2 className="font-serif text-2xl text-ink">
        ✓ {data.subscription!.planName} complete
      </h2>
      <p className="text-soft mt-2">You read the whole thing. Well done.</p>
      <button
        onClick={() => pickAnother.mutate()}
        disabled={pickAnother.isPending}
        className="mt-4 h-10 px-5 rounded-full border border-line text-sm text-ink"
      >
        {pickAnother.isPending ? 'Loading…' : 'Pick another plan'}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------- Week card
function WeekCard({ data }: { data: ReadingPlanMeResponse }) {
  const qc = useQueryClient();
  const catchUp = useMutation({
    mutationFn: () => api.post('/reading-plans/me/catch-up', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] }),
  });

  const sub = data.subscription!;
  const week = data.week;
  const todayDay = (week?.currentDay ?? 1) + data.behindBy;

  // Selected day for the passages panel. Defaults to currentDay; user
  // can tap any pill in the strip to peek at a different day's reading.
  const [selectedDayNum, setSelectedDayNum] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const activeDayNum = selectedDayNum ?? week?.currentDay ?? null;
  const activeDay =
    week?.days.find((d) => d.dayNum === activeDayNum) ?? week?.days[0] ?? null;

  return (
    <section className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-serif text-2xl text-ink">Reading Plan</h2>
          <p className="text-xs text-muted mt-1">
            {sub.planName}
            {data.streak > 0 ? ` · ${data.streak}-day streak` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          aria-label="Reading plan settings"
          className="text-muted hover:text-ink shrink-0 p-1 -m-1"
        >
          <GearIcon className="w-5 h-5" />
        </button>
      </div>

      {settingsOpen && (
        <SettingsSheet data={data} onClose={() => setSettingsOpen(false)} />
      )}

      {data.behindBy > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-oxblood/10 border border-oxblood/40">
          <p className="text-sm text-ink">
            You're{' '}
            <strong>
              {data.behindBy} day{data.behindBy === 1 ? '' : 's'}
            </strong>{' '}
            behind. Today is Day {todayDay}; you're on Day {week?.currentDay}.
          </p>
          <button
            onClick={() => catchUp.mutate()}
            disabled={catchUp.isPending}
            className="mt-2 h-9 px-4 rounded-full bg-oxblood text-paper text-xs font-medium disabled:opacity-50"
          >
            {catchUp.isPending ? 'Catching up…' : 'Catch up to today'}
          </button>
        </div>
      )}

      {!week || week.days.length === 0 ? (
        <p className="text-soft mt-4 italic">
          This week's readings haven't been published yet. Check back soon.
        </p>
      ) : (
        <>
          {/* Always 7 pills with currentDay at the visual center (index
              3). Pad with disabled placeholders when the plan-day window
              extends past [1, totalDays]. Grid-of-7 fills the card
              width so the buttons are large enough to tap easily. */}
          <div className="mt-5 grid grid-cols-7 gap-1.5">
            {buildSevenDayWindow(week.currentDay, week.days, sub.totalDays).map(
              (slot) => {
                const day = slot.day;
                if (!day) {
                  return (
                    <PlaceholderPill key={`pad-${slot.dayNum}`} dayNum={slot.dayNum} />
                  );
                }
                return (
                  <DayPill
                    key={day.dayNum}
                    day={day}
                    selected={day.dayNum === activeDayNum}
                    onClick={() => setSelectedDayNum(day.dayNum)}
                  />
                );
              },
            )}
          </div>

          {activeDay && (
            <>
              <p className="kicker text-sage mt-5 text-center">
                Day {activeDay.dayNum} of {sub.totalDays}
              </p>
              <div className="mt-3 space-y-2">
                {activeDay.passages.map((p) => (
                  <PassageRow
                    key={p.passageIndex}
                    dayNum={activeDay.dayNum}
                    passage={p}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {data.family.length > 1 && <FamilyRow family={data.family} />}
    </section>
  );
}

/** Builds the always-7 day window centered on `currentDay`. Slots whose
 *  computed dayNum falls outside [1, totalDays] return `day: null` so
 *  the renderer can show a disabled placeholder pill. */
function buildSevenDayWindow(
  currentDay: number,
  apiDays: ReadingPlanWeekDay[],
  totalDays: number,
): { dayNum: number; day: ReadingPlanWeekDay | null }[] {
  const byDayNum = new Map(apiDays.map((d) => [d.dayNum, d]));
  const out: { dayNum: number; day: ReadingPlanWeekDay | null }[] = [];
  for (let offset = -3; offset <= 3; offset++) {
    const dayNum = currentDay + offset;
    const inRange = dayNum >= 1 && dayNum <= totalDays;
    out.push({ dayNum, day: inRange ? (byDayNum.get(dayNum) ?? null) : null });
  }
  return out;
}

function DayPill({
  day,
  selected,
  onClick,
}: {
  day: ReadingPlanWeekDay;
  selected: boolean;
  onClick: () => void;
}) {
  // Visual priority: SELECTED beats everything (so the user sees the
  // tap landed). Then fully-done. Then today's ring. Then default.
  const className = selected
    ? 'bg-oxblood text-paper border-oxblood'
    : day.fullyDone
      ? 'bg-sage text-paper border-sage'
      : day.isCurrent
        ? 'bg-paper text-sage border-sage'
        : 'bg-paper text-soft border-line';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Day ${day.dayNum}${day.isCurrent ? ' (today)' : ''}${day.fullyDone ? ' — complete' : ''}`}
      aria-current={day.isCurrent ? 'date' : undefined}
      className={`aspect-square w-full rounded-full border-2 text-sm font-medium flex items-center justify-center transition-colors ${className}`}
    >
      {day.dayNum}
    </button>
  );
}

/** Visually-disabled slot rendered when the 7-day window extends past
 *  [1, totalDays]. Keeps `today` at index 3 of the row. */
function PlaceholderPill({ dayNum }: { dayNum: number }) {
  return (
    <div
      aria-hidden
      className="aspect-square w-full rounded-full border-2 border-dashed border-line/60 bg-paper/60 text-muted/40 text-xs font-medium flex items-center justify-center select-none"
    >
      {dayNum < 1 ? '·' : '·'}
    </div>
  );
}

function PassageRow({
  dayNum,
  passage,
}: {
  dayNum: number;
  passage: ReadingPlanPassage;
}) {
  const qc = useQueryClient();
  const mark = useMutation({
    mutationFn: () =>
      api.put(`/reading-plans/me/passages/${dayNum}/${passage.passageIndex}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] }),
  });
  const unmark = useMutation({
    mutationFn: () =>
      api.delete(`/reading-plans/me/passages/${dayNum}/${passage.passageIndex}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] }),
  });
  const toggle = () => {
    if (passage.completed) unmark.mutate();
    else mark.mutate();
  };
  const onReadClick = () => {
    // Optimistically mark done before navigating so the user sees the
    // check immediately. The mutation is idempotent server-side.
    if (!passage.completed) mark.mutate();
    window.open(passage.youversionUrl, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={toggle}
        disabled={mark.isPending || unmark.isPending}
        aria-label={passage.completed ? 'Mark unread' : 'Mark read'}
        className={`w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center ${
          passage.completed ? 'bg-sage border-sage text-paper' : 'border-muted bg-paper'
        }`}
      >
        {passage.completed && <span className="text-xs leading-none">✓</span>}
      </button>
      <span
        className={`flex-1 font-serif text-base ${passage.completed ? 'text-soft line-through' : 'text-ink'}`}
      >
        {passage.passageRef}
      </span>
      <button
        type="button"
        onClick={onReadClick}
        aria-label={`Read ${passage.passageRef} in YouVersion`}
        className="text-oxblood shrink-0 p-1 -m-1"
      >
        <ExternalLinkIcon className="w-5 h-5" />
      </button>
    </div>
  );
}

/** External-link icon — small box + arrow leaving the upper-right.
 *  Matches the hand-drawn line style used elsewhere in the app. */
function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden>
      <path
        d="M14 4h6v6M20 4L11 13M5 7v12a1 1 0 0 0 1 1h12"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FamilyRow({ family }: { family: ReadingPlanMeResponse['family'] }) {
  return (
    <div className="mt-5 flex items-center gap-1.5">
      {family.map((m) => {
        const initials = m.displayName
          .split(/\s+/)
          .map((s) => s[0]?.toUpperCase() ?? '')
          .slice(0, 2)
          .join('');
        return (
          <span
            key={m.memberId}
            title={`${m.displayName} · Day ${m.currentDay}${m.completedToday ? ' · done today' : ''}`}
            className={`w-8 h-8 rounded-full text-xs font-medium flex items-center justify-center ${
              m.completedToday ? 'bg-sage text-paper' : 'bg-line text-soft'
            }`}
          >
            {initials || '·'}
          </span>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------- Settings sheet
function SettingsSheet({
  data,
  onClose,
}: {
  data: ReadingPlanMeResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const auth = useAuth();
  const me = auth.state === 'signed-in' ? auth.me : null;
  const catalog = useQuery(readingPlansListQueryOptions());

  const sub = data.subscription!;
  const week = data.week;
  const completedPct =
    week && sub.totalDays > 0
      ? Math.round(((week.currentDay - 1) / sub.totalDays) * 100)
      : 0;
  const onTimePct =
    data.stats.daysCompleted > 0
      ? Math.round((data.stats.daysCompletedOnTime / data.stats.daysCompleted) * 100)
      : 0;

  const switchPlan = useMutation({
    mutationFn: (planSlug: string) =>
      api.post<{ changed: boolean }>('/reading-plans/me/subscribe', { planSlug }),
    onSuccess: (result) => {
      setPendingSwitch(null);
      void qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] });
      // Close the sheet so the user sees the WeekCard re-render with the
      // new plan name + Day 1 — otherwise the modal covers the change
      // and it looks like nothing happened. Only close on an actual
      // plan change (same-plan resubscribe is a no-op).
      if (result.changed) onClose();
    },
  });
  const setTranslation = useMutation({
    mutationFn: (translation: string) => api.patch('/me', { translation }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      // YouVersion URLs are rebuilt server-side from member.translation,
      // so the plan response needs to refetch too.
      void qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] });
    },
  });
  const leavePlan = useMutation({
    mutationFn: () => api.delete('/reading-plans/me/subscribe'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reading-plan', 'me'] });
      onClose();
    },
  });
  const [confirmLeave, setConfirmLeave] = useState(false);
  // One switch-confirm dialog for the picker, keyed by the target plan.
  const [pendingSwitch, setPendingSwitch] = useState<ReadingPlanCatalogEntry | null>(
    null,
  );

  return (
    <>
      <Sheet open onClose={onClose} title="Reading Plan Settings">
        <div className="px-5 py-5 space-y-6">
          {/* Progress */}
          <section>
            <h3 className="kicker text-sage mb-2">Progress</h3>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-ink">
                Day {week?.currentDay ?? sub.totalDays} of {sub.totalDays}
              </span>
              <span className="text-sm text-muted">{completedPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-line overflow-hidden">
              <div
                className="h-full bg-sage"
                style={{ width: `${Math.min(100, completedPct)}%` }}
              />
            </div>
          </section>

          {/* Stats */}
          <section>
            <h3 className="kicker text-sage mb-2">Statistics</h3>
            <dl className="grid grid-cols-2 gap-3">
              <Stat label="Days completed" value={String(data.stats.daysCompleted)} />
              <Stat
                label="On time"
                value={`${data.stats.daysCompletedOnTime} (${onTimePct}%)`}
              />
              <Stat
                label="Current streak"
                value={data.streak > 0 ? `${data.streak} days` : '—'}
              />
              <Stat
                label="Started"
                value={new Date(sub.startDate + 'T00:00:00').toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              />
            </dl>
          </section>

          {/* Plan picker */}
          <section>
            <h3 className="kicker text-sage mb-2">Plan</h3>
            <p className="text-xs text-muted mb-3">
              Switching plans keeps your progress on the old plan; if you come back to it
              later it picks up where you left off.
            </p>
            <div className="space-y-2">
              {(catalog.data?.plans ?? []).map((p) => (
                <PlanOption
                  key={p.slug}
                  plan={p}
                  active={p.slug === sub.planSlug}
                  onSelect={() => setPendingSwitch(p)}
                  pending={switchPlan.isPending}
                />
              ))}
            </div>
          </section>

          {/* Translation */}
          <section>
            <h3 className="kicker text-sage mb-2">Bible translation</h3>
            <p className="text-xs text-muted mb-3">
              Used for every "Read in YouVersion" link, on this plan and across the rest
              of the app.
            </p>
            <div className="flex flex-wrap gap-2">
              {TRANSLATIONS.map((t) => {
                const active = (me?.member.translation ?? 'CSB') === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTranslation.mutate(t)}
                    disabled={setTranslation.isPending || active}
                    className={`h-9 px-4 rounded-full text-xs font-medium border ${
                      active
                        ? 'bg-ink text-paper border-ink'
                        : 'bg-paper text-ink border-line hover:bg-line/40'
                    } disabled:opacity-100`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Leave */}
          <section className="pt-2 border-t border-line">
            <button
              type="button"
              onClick={() => setConfirmLeave(true)}
              disabled={leavePlan.isPending}
              className="text-sm text-oxblood underline disabled:opacity-50"
            >
              {leavePlan.isPending ? 'Leaving…' : 'Leave reading plan'}
            </button>
          </section>
        </div>
      </Sheet>

      {confirmLeave && (
        <ConfirmDialog
          title="Leave this reading plan?"
          message="Your progress is kept — you can resume by re-picking the plan later."
          confirmLabel="Leave"
          destructive
          busy={leavePlan.isPending}
          error={leavePlan.isError ? 'Could not leave. Try again.' : null}
          onConfirm={() => leavePlan.mutate()}
          onCancel={() => setConfirmLeave(false)}
        />
      )}

      {pendingSwitch && (
        <ConfirmDialog
          title={`Switch to ${pendingSwitch.name}?`}
          message="You'll start at Day 1 of the new plan; progress on the current plan is kept."
          confirmLabel="Switch"
          busy={switchPlan.isPending}
          error={switchPlan.isError ? 'Could not switch. Try again.' : null}
          onConfirm={() => switchPlan.mutate(pendingSwitch.slug)}
          onCancel={() => setPendingSwitch(null)}
        />
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="font-serif text-lg text-ink mt-0.5">{value}</div>
    </div>
  );
}

function PlanOption({
  plan,
  active,
  onSelect,
  pending,
}: {
  plan: ReadingPlanCatalogEntry;
  active: boolean;
  onSelect: () => void;
  pending: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (active) return;
        onSelect();
      }}
      disabled={pending}
      className={`w-full p-3 rounded-lg border-2 text-left disabled:opacity-50 ${
        active ? 'border-oxblood bg-oxblood/10' : 'border-line bg-paper hover:bg-line/40'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-serif text-base text-ink">{plan.name}</span>
        {active && <span className="text-xs text-oxblood">Current</span>}
      </div>
      <div className="text-xs text-muted mt-0.5">
        {plan.totalDays} days · ~{durationLabel(plan.totalDays)}
      </div>
    </button>
  );
}

/** Settings gear — hand-drawn line style consistent with the rest of
 *  the app's icons (see TabBar, ScreenHeader). */
function GearIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.7} />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 4.37 16.96l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinejoin="round"
      />
    </svg>
  );
}
